import styled from "@emotion/styled";
import type { TextFieldProps } from "@mui/material";
import { TextField } from "@mui/material";
import { useField } from "formik";
import type { ForwardedRef } from "react";
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from "react";

import { useClickShield } from "../../pointer/ClickShieldContext";
import { ClickShieldProvider, type ClickShieldProps } from "../../pointer/ClickShieldProvider";
import { useFieldContext } from "../FormFieldContext";
import { useCommitChange, useFieldLabelAndDescription, useFieldPlaceholder } from "./helpers";

export const ShieldedTextarea = styled(TextField)`
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  font-size: inherit;

  height: 100%;
  width: 100%;

  resize: none;
`;

type FieldTextareaProps = Omit<
  TextFieldProps,
  "id" | "name" | "value" | "onBlur" | "onChange" | "onKeyPress" | "label" | "helpText"
> & {
  onChangeCommitted?: (value?: string, e?: React.FocusEvent<HTMLInputElement>) => void;
};

export type FieldShieldedTextareaActions = {
  focus: () => void;
};

export const FieldShieldedTextarea = forwardRef<FieldShieldedTextareaActions, FieldTextareaProps & ClickShieldProps>(
  (props, reference) => {
    const { onShieldClick, onShieldDoubleClick, ...otherProps } = props;

    return (
      <ClickShieldProvider onShieldClick={onShieldClick} onShieldDoubleClick={onShieldDoubleClick}>
        <ShieldedTextareaField {...{ ...otherProps, reference }} />
      </ClickShieldProvider>
    );
  },
);
FieldShieldedTextarea.displayName = "FieldShieldedTextarea";

const ShieldedTextareaField = ({
  reference,
  ...props
}: FieldTextareaProps & { reference: ForwardedRef<FieldShieldedTextareaActions> }) => {
  const ref = useRef<HTMLTextAreaElement>(null);

  const textareaRef = useMemo(() => (typeof props.inputRef !== "function" ? (props.inputRef ?? ref) : ref), [props]);

  const { activate, deactivate } = useClickShield();

  const setFocusToEndOfTextarea = useCallback(() => {
    const textarea = textareaRef.current;

    if (!textarea) return;

    textarea.setSelectionRange(textarea.value.length, textarea.value.length);
    textarea.focus();
  }, [textareaRef]);

  const handleImperativeFocus = useCallback(() => {
    deactivate();
    setFocusToEndOfTextarea();
    textareaRef.current?.focus();
  }, [deactivate, setFocusToEndOfTextarea, textareaRef]);

  useImperativeHandle(reference, () => ({ focus: handleImperativeFocus }));

  const { onChangeCommitted, minRows = 1, maxRows = 5, placeholder, ...otherProps } = props;

  const commitChange = useCommitChange();

  const { controlId, dataTest, name } = useFieldContext();
  const [{ value, onChange }] = useField(name);

  const fieldPlaceholder = useFieldPlaceholder(name);
  const [label, helperText] = useFieldLabelAndDescription(name);

  const handleBlur: TextFieldProps["onBlur"] = useCallback(() => {
    onChangeCommitted?.(value);
    commitChange();
    activate();
  }, [activate, commitChange, onChangeCommitted, value]);

  return (
    <ShieldedTextarea
      inputRef={textareaRef}
      size="small"
      {...otherProps}
      id={controlId}
      name={name}
      value={value}
      placeholder={placeholder ?? fieldPlaceholder}
      onBlur={handleBlur}
      onChange={onChange}
      onClick={(event) => event.stopPropagation()}
      slotProps={{
        ...otherProps.slotProps,
        htmlInput: {
          ...otherProps.slotProps?.htmlInput,
          "data-test": `${dataTest ?? name}-shielded-textarea`,
        } as any,
      }}
      multiline={!!(props.minRows || props.maxRows)}
      label={label}
      helperText={helperText}
      minRows={minRows}
      maxRows={minRows > maxRows ? undefined : maxRows}
    />
  );
};
